import { BUSINESS, MEDIA } from "@/data";
import { Logo } from "./Buttons";
import * as Icons from "lucide-react";

const QUICK_LINKS = [
  { label: "Repairs", href: "#pricing" },
  { label: "Unlocked Phones", href: "#products" },
  { label: "Accessories", href: "#products" },
  { label: "Sell Your Phone", href: "#sell" },
  { label: "Prepaid Service", href: "#prepaid" },
  { label: "Contact", href: "#visit" },
];

const LEGAL = [
  { label: "Privacy Policy", href: "#" },
  { label: "Terms", href: "#" },
  { label: "Warranty Policy", href: "#" },
];

export default function Footer({ onOpenQuote }) {
  return (
    <footer data-testid="site-footer" className="relative pt-20 pb-32 md:pb-12 px-4 md:px-8 bg-brand-navy text-white">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-brand-red/15 blur-[120px] pointer-events-none" aria-hidden />

      <div className="relative max-w-7xl mx-auto">
        {/* Final CTA */}
        <div className="bg-gradient-to-br from-white/[0.06] to-white/[0.02] backdrop-blur-md border border-white/10 rounded-3xl md:rounded-[2rem] p-8 md:p-14 mb-16 text-center md:text-left flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="final-cta">
          <div>
            <h3 className="font-display text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1]">
              Need your phone fixed or<br />looking for your next device?
            </h3>
            <p className="mt-3 text-white/70">Call <a href={`tel:${BUSINESS.phoneTel}`} className="text-brand-red font-bold hover:underline">{BUSINESS.phone}</a> or visit Your Phone Plug today.</p>
          </div>
          <a
            href={`tel:${BUSINESS.phoneTel}`}
            data-testid="footer-call-btn"
            className="shrink-0 inline-flex items-center gap-2 font-bold rounded-full py-4 px-8 bg-brand-red hover:brightness-110 text-white text-lg animate-pulse-red transition-all active:scale-95"
          >
            <Icons.Phone className="w-5 h-5" />
            Call Now
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-2.5 mb-4">
              <Logo size="lg" />
              <div>
                <div className="font-display font-extrabold text-lg text-white">Your Phone Plug</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-brand-red font-bold">Miami Gardens, FL</div>
              </div>
            </div>
            <p className="text-sm text-white/65 max-w-md leading-relaxed">
              Local cellphone repair, unlocked phones, accessories, prepaid service, and honest tech help. {BUSINESS.tagline}.
            </p>
            <div className="mt-5 flex items-center gap-2">
              <a href={BUSINESS.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-ig" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 grid place-items-center hover:bg-brand-red hover:border-brand-red transition-all">
                <Icons.Instagram className="w-4.5 h-4.5 text-white" />
              </a>
              <a href={`tel:${BUSINESS.phoneTel}`} data-testid="footer-phone" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 grid place-items-center hover:bg-brand-red hover:border-brand-red transition-all">
                <Icons.Phone className="w-4.5 h-4.5 text-white" />
              </a>
              <a href={`mailto:${BUSINESS.email}`} data-testid="footer-mail" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 grid place-items-center hover:bg-brand-red hover:border-brand-red transition-all">
                <Icons.Mail className="w-4.5 h-4.5 text-white" />
              </a>
              <a href={BUSINESS.directionsUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-map" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 grid place-items-center hover:bg-brand-red hover:border-brand-red transition-all">
                <Icons.MapPin className="w-4.5 h-4.5 text-white" />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <h5 className="font-display uppercase text-xs tracking-[0.2em] text-white/50 mb-4 font-bold">Quick Links</h5>
            <ul className="space-y-2.5">
              {QUICK_LINKS.map((l) => (
                <li key={l.label}>
                  <a href={l.href} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm text-white/75 hover:text-brand-red transition-colors">
                    {l.label}
                  </a>
                </li>
              ))}
              <li>
                <button onClick={onOpenQuote} data-testid="footer-quote-btn" className="text-sm text-brand-red hover:brightness-125 transition-colors font-bold">
                  Get a Quote →
                </button>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <h5 className="font-display uppercase text-xs tracking-[0.2em] text-white/50 mb-4 font-bold">Visit Us</h5>
            <address className="not-italic text-sm text-white/75 space-y-2">
              <div>{BUSINESS.address}</div>
              <div className="font-mono">{BUSINESS.phone}</div>
              <div className="break-all">{BUSINESS.email}</div>
            </address>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-xs text-white/50">
            © {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.
          </div>
          <div className="flex items-center gap-5">
            {LEGAL.map((l) => (
              <a key={l.label} href={l.href} className="text-xs text-white/50 hover:text-white transition-colors">
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
